import { useState } from 'react';
import { AuthError, authService } from '../../services/authService';

interface LoginPageProps {
  onLoginSuccess: () => void;
  onRegisterClick?: () => void;
}

export function LoginPage({ onLoginSuccess, onRegisterClick }: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await authService.login({ username, password });
      onLoginSuccess();
    } catch (err) {
      const authError = err as AuthError;
      setError(authError.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&family=Rajdhani:wght@300;400;500;600;700&display=swap');

        .login-card {
          position: relative;
          z-index: 2;
          background: rgba(17, 24, 39, 0.92);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 40px;
          padding: 3rem 2.5rem;
          width: 100%;
          max-width: 450px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        }

        .login-title {
          text-align: center;
          margin-bottom: 2.5rem;
        }

        .login-title h1 {
          font-family: 'Orbitron', monospace;
          font-size: 2.5rem;
          font-weight: 800;
          color: rgba(255, 255, 255, 0.95);
          margin-bottom: 0.5rem;
          letter-spacing: 0.1em;
          text-shadow: 0 2px 8px rgba(183, 63, 63, 0.5);
        }

        .login-title p {
          font-family: 'Rajdhani', sans-serif;
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.875rem;
          font-weight: 500;
          letter-spacing: 0.15em;
          text-transform: uppercase;
        }

        .input-group {
          margin-bottom: 1.5rem;
        }

        .input-label {
          display: block;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.75rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          font-family: 'Rajdhani', sans-serif;
        }

        .input-field {
          width: 100%;
          padding: 1rem 1.25rem;
          background: rgba(31, 41, 55, 0.5);
          border: 2px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          color: rgba(255, 255, 255, 0.95);
          font-size: 0.9375rem;
          font-weight: 500;
          transition: all 0.25s ease;
          outline: none;
        }

        .input-field:hover {
          border-color: rgba(255, 255, 255, 0.2);
          background: rgba(31, 41, 55, 0.7);
        }

        .input-field:focus {
          border-color: #B73F3F;
          background: rgba(31, 41, 55, 0.8);
          box-shadow: 0 0 0 4px rgba(183, 63, 63, 0.2);
        }

        .input-field::placeholder {
          color: rgba(255, 255, 255, 0.4);
          font-weight: 400;
        }

        .input-field:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          background: rgba(31, 41, 55, 0.3);
        }

        .error-message {
          background: rgba(220, 38, 38, 0.15);
          border: 1px solid rgba(252, 165, 165, 0.4);
          color: rgba(252, 165, 165, 0.95);
          padding: 1rem 1.25rem;
          border-radius: 14px;
          font-size: 0.875rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .login-button {
          width: 100%;
          padding: 1.125rem;
          background: linear-gradient(135deg, #B73F3F 0%, #8a2a2a 100%);
          border: none;
          border-radius: 24px;
          color: white;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
          letter-spacing: 0.01em;
          box-shadow: 0 10px 30px rgba(183, 63, 63, 0.4);
          margin-top: 0.5rem;
        }

        .login-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 40px rgba(183, 63, 63, 0.6);
          background: linear-gradient(135deg, #c14545 0%, #a33535 100%);
        }

        .login-button:active {
          transform: translateY(0);
        }

        .login-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .login-footer {
          margin-top: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          text-align: center;
        }

        .login-hint {
          color: rgba(255, 255, 255, 0.6);
          font-size: 0.875rem;
          font-weight: 500;
        }

        .login-hint strong {
          color: rgba(255, 255, 255, 0.8);
          font-weight: 600;
        }

        .register-link {
          margin-top: 1.5rem;
          text-align: center;
          padding-top: 1.5rem;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .register-link button {
          background: none;
          border: none;
          color: rgba(255, 255, 255, 0.6);
          cursor: pointer;
          text-decoration: underline;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .register-link button:hover {
          color: #B73F3F;
        }
      `}</style>

      <div className="login-card">
        <div className="login-title">
          <h1>WIZ OCM</h1>
          <p>Wizbase Oracle Cloud Manager</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="input-group">
            <label htmlFor="username" className="input-label">
              아이디
            </label>
            <input
              id="username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="input-field"
              placeholder="아이디를 입력하세요"
              required
              disabled={loading}
              autoComplete="username"
            />
          </div>

          <div className="input-group">
            <label htmlFor="password" className="input-label">
              비밀번호
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field"
              placeholder="비밀번호를 입력하세요"
              required
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="login-button"
            disabled={loading}
          >
            {loading ? '로그인 중...' : '로그인'}
          </button>
        </form>

        <div className="login-footer">
          <div className="login-hint">
            <strong>문의: jajeong@wizbase.co.kr</strong>
          </div>
        </div>

        {onRegisterClick && (
          <div className="register-link">
            <button type="button" onClick={onRegisterClick}>
              계정이 없으신가요? 회원가입
            </button>
          </div>
        )}
      </div>
    </>
  );
}
